import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, Alert } from '@mui/material'
import ConsultasCerradasHead from '../components/ConsultasCerradasHead'
import ConsultasCerradasTable from '../components/ConsultasCerradasTable'
import PuntuacionDialog from '../components/PuntuacionDialog'
import SeguimientoContainer from '../../Vendedor/containers/SeguimientoContainer'
import { apiFetch } from '../../api'
import ReassignDialog from '../components/ReassignDialog'
import socket from '../../socket'

const DEFAULT_FILTERS = { VENTA: true, BAJA: true, FUTURO: true }

export default function ConsultasCerradasContainer({
  onBackHome,
  onBackNoAsignados,
  onOpenClientes,
  isFullScreen,
  onToggleFullScreen,
}) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Paginación
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState(20)

  useEffect(() => {
    if (isFullScreen) {
      setLimit(100);
    } else {
      setLimit(20);
    }
    setPage(0);
  }, [isFullScreen]);

  // Estados de filtros
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [vendedorFilter, setVendedorFilter] = useState('todos')
  const [vendedores, setVendedores] = useState([])

  // Estados de modales
  const [openSeg, setOpenSeg] = useState(false)
  const [segAsign, setSegAsign] = useState(null)
  // Puntuacion dialog
  const [openPunt, setOpenPunt] = useState(false)
  const [puntAsign, setPuntAsign] = useState(null)


  // Cargar lista de vendedores al montar el componente (solo activos)
  const fetchVendedores = useCallback(async () => {
    try {
      const res = await apiFetch('/api/vendedores/listado?ocultar_inactivos=true&limit=1000', { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const lista = Array.isArray(data) ? data : data.items || []
      const activos = lista.filter(v => v.activo !== false)
      setVendedores(activos)
    } catch (e) {
      console.error('Error cargando vendedores:', e)
      setVendedores([])
    }
  }, [])

  useEffect(() => {
    fetchVendedores()
  }, [fetchVendedores])

  // Construir parámetros de consulta
  const estadosCsv = useMemo(() => {
    const activos = Object.entries(filters).filter(([_, v]) => v).map(([k]) => k)
    const finalList = activos.length ? activos : Object.keys(DEFAULT_FILTERS)
    return finalList.join(',')
  }, [filters])

  // Reset de página cuando cambian filtros / vendedor
  // (evita quedarte en página 4 y ver vacío al cambiar filtros)
  useEffect(() => {
    setPage(0)
  }, [estadosCsv, vendedorFilter])

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Construir URL con parámetros
      const params = new URLSearchParams({
        estados: estadosCsv,
        page: String(page + 1), // Base 1 para API
        limit: String(limit)
      })

      // Agregar filtro de vendedor si no es "todos"
      if (vendedorFilter !== 'todos') {
        params.append('id_vendedor', vendedorFilter)
      }

      const res = await apiFetch(`/api/consultas_cerradas?${params.toString()}`, {
        credentials: 'include',
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      setRows(Array.isArray(data.items) ? data.items : [])
      setTotal(data.total || 0)

    } catch (e) {
      console.error('Error cargando consultas cerradas:', e)
      setError('No se pudieron cargar las consultas cerradas.')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [estadosCsv, vendedorFilter, page, limit])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  // Listen socket for vendor toggles to refresh rows when necessary
  useEffect(() => {
    if (!socket) return
    const handler = (data) => {
      try {
        if (!data) return
        const vid = data.vendedor_id
        if (!vid) return
        // If any row belongs to this vendor, refresh
        if (rows.some(r => (r.vendedor_id ?? r.id_vendedor) === vid)) {
          fetchRows()
        }
      } catch (e) { console.error('socket vendedor_toggled handler error', e) }
    }
    socket.on('vendedor_toggled', handler)
    return () => socket.off('vendedor_toggled', handler)
  }, [rows, fetchRows])

  // Paginación Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  // Cambiar estado
  const handleEstadoChange = async (row, newEstado) => {
    const id = row?.id_ven_cli ?? row?.id
    if (!id) return

    // Si vuelve a ACTIVO, aplicarlo inmediatamente
    if (newEstado === 'ACTIVO') {
      // Si el vendedor actual está inactivo, forzar reasignación antes de reabrir
      const isInactive = (row?.vendedor_activo === false || row?.vendedor_activo === 0)
      if (isInactive) {
        // Abrir modal de reasignación de forma obligatoria
        handleOpenReassign(row, true)
        return
      }

      setRows(prev => prev.map(r => ((r.id_ven_cli ?? r.id) === id ? { ...r, estado: newEstado } : r)))
      try {
        const res = await apiFetch(`/api/leads/${id}/estado`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ estado: newEstado }),
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        // Si vuelve a ACTIVO, sacarlo de esta vista
        setRows(prev => prev.filter(r => (r.id_ven_cli ?? r.id) !== id))
        return
      } catch (e) {
        console.error('Error actualizando estado a ACTIVO:', e)
        await fetchRows()
        return
      }
    }

    // Para estados distintos de ACTIVO: primero pedir puntuación y luego cambiar estado
    setPuntAsign({ row, id, pendingEstado: newEstado })
    setOpenPunt(true)
  }

  const handleSubmitPuntuacion = async (score) => {
    if (!puntAsign?.id) {
      setOpenPunt(false)
      setPuntAsign(null)
      return
    }

    try {
      const payload = { estado: puntAsign.pendingEstado ?? 'VENTA', puntuacion_venta: Number(score) }
      const res = await apiFetch(`/api/asignaciones/${puntAsign.id}/cerrar`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }

      const data = await res.json().catch(() => null)
      if (data) {
        setRows(prev => prev.map(r => ((r.id_ven_cli ?? r.id) === puntAsign.id ? { ...r, puntuacion_venta: data.puntuacion_venta ?? Number(score), estado: data.estado ?? (puntAsign.pendingEstado ?? r.estado) } : r)))
      }

    } catch (e) {
      console.error('Error enviando puntuación y cerrando asignación:', e)
      await fetchRows()
    } finally {
      setOpenPunt(false)
      setPuntAsign(null)
    }
  }

  const handleOpenSeguimiento = (row) => {
    setSegAsign(row);
    setOpenSeg(true);
  };

  // Reassign modal state & handlers
  const [openReassign, setOpenReassign] = React.useState(false)
  const [reassignAsign, setReassignAsign] = React.useState(null)
  const [reassignObligatory, setReassignObligatory] = React.useState(false)
  const [reassignShouldReopen, setReassignShouldReopen] = React.useState(false)
  const [reassignHelperText, setReassignHelperText] = React.useState('')
  const [reassignNotifId, setReassignNotifId] = React.useState(null)

  const getVendedorName = (row) => {
    return (
      row?.vendedor_nombre ??
      row?.nombre_vendedor ??
      row?.vendedorNombre ??
      row?.vendedor?.nombre ??
      row?.vendedor?.nombre_completo ??
      row?.nombreVendedor ??
      ''
    )
  }

  const handleOpenReassign = useCallback((row, shouldReopen = false, helperText = '', notifId = null) => {
    setReassignAsign(row)
    // Si el vendedor actual está inactivo, marcar como obligatorio
    const isInactive = (row?.vendedor_activo === false || row?.vendedor_activo === 0)
    setReassignObligatory(Boolean(isInactive))
    setReassignShouldReopen(Boolean(shouldReopen))
    setReassignHelperText(helperText)
    setReassignNotifId(notifId)
    setOpenReassign(true)
  }, [])

  // Listen for external requests to open the reassign modal (e.g. from notifications)
  React.useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {}
      const ven_clien_id = detail?.ven_clien_id
      const cliente_id = detail?.cliente_id
      if (!ven_clien_id) return

      const shouldReopen = Boolean(detail?.shouldReopen || detail?.reopenAfterReassign)
      const found = rows.find(r => (r.id_ven_cli ?? r.id) === ven_clien_id)
      const vendedorName = detail?.vendedor_nombre || detail?.nombre_vendedor || getVendedorName(found)
      const helperText = vendedorName
        ? `El vendedor ${vendedorName} asignado a este cliente no está más activo. Seleccioná otro vendedor para continuar.`
        : 'El vendedor asignado a este cliente no está más activo. Seleccioná otro vendedor para continuar.'

      if (found) {
        handleOpenReassign(found, shouldReopen, helperText, detail?.notif_id ?? null)
      } else {
        handleOpenReassign({ id_ven_cli: ven_clien_id, cliente_id: cliente_id, vendedor_activo: false, vendedor_id: detail?.vendedor_id ?? null }, shouldReopen, helperText, detail?.notif_id ?? null)
      }
    }

    window.addEventListener('open_reassign_for_ven_clien', handler)
    return () => window.removeEventListener('open_reassign_for_ven_clien', handler)
  }, [rows, handleOpenReassign])

  const handleCloseReassign = () => {
    setReassignAsign(null)
    setOpenReassign(false)
    setReassignObligatory(false)
    setReassignShouldReopen(false)
    setReassignHelperText('')
    setReassignNotifId(null)
  }

  const handleDoReassign = async (newVendedorId) => {
    if (!reassignAsign) return

    const id_ven_cli = reassignAsign.id_ven_cli ?? reassignAsign.id
    const cliente_id = reassignAsign.cliente_id ?? reassignAsign.id_cliente ?? reassignAsign.clienteId
    const vendedor_anterior_id = reassignAsign.vendedor_id ?? reassignAsign.id_vendedor

    try {
      const payload = {
        id_nuevo_vendedor: Number(newVendedorId),
        vendedor_anterior_id: Number(vendedor_anterior_id),
        cliente_id: Number(cliente_id),
      }
      const useAtomicReopen = reassignShouldReopen || reassignNotifId
      const endpoint = useAtomicReopen
        ? `/api/asignaciones/${id_ven_cli}/reasignar-y-reactivar`
        : `/api/asignaciones/${id_ven_cli}/reasignar`
      const res = await apiFetch(endpoint, {
        method: useAtomicReopen ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          notif_id: reassignNotifId || null,
        }),
        credentials: 'include',
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }
      await fetchRows()
    } catch (e) {
      console.error('Error reasignando:', e)
      throw e
    } finally {
      handleCloseReassign()
    }
  }

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden',
      px: isFullScreen ? 2 : 5,
      pt: isFullScreen ? 3 : 4,
      transition: 'padding 0.3s ease'
    }}>
      <ConsultasCerradasHead
        filters={filters}
        onChangeFilters={setFilters}
        vendedorFilter={vendedorFilter}
        onChangeVendedorFilter={setVendedorFilter}
        vendedores={vendedores}
        onBackHome={onBackHome}
        onBackNoAsignados={onBackNoAsignados}
        onOpenClientes={onOpenClientes}
        isFullScreen={isFullScreen}
        onToggleFullScreen={onToggleFullScreen}
      />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ConsultasCerradasTable
          rows={rows}
          onOpenSeguimiento={handleOpenSeguimiento}
          onEstadoChange={handleEstadoChange}
          onOpenReassign={handleOpenReassign}
          count={total}
          page={page}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          customHeight={isFullScreen ? "flex" : undefined}
        />
    </Box>
      )}

      <PuntuacionDialog
        open={openPunt}
        initial={puntAsign?.row?.puntuacion_venta ?? 0}
        onClose={() => { setOpenPunt(false); setPuntAsign(null); }}
        onSubmit={handleSubmitPuntuacion}
      />

      {/* Modales */}
      <SeguimientoContainer
        open={Boolean(openSeg && segAsign)}
        onClose={() => { setOpenSeg(false); setSegAsign(null); }}
        asignacion={segAsign ?? null}
        adminReadOnly={true}
      />
      <ReassignDialog
        open={openReassign}
        onClose={handleCloseReassign}
        vendedores={vendedores}
        asignacion={reassignAsign}
        onReassign={handleDoReassign}
        obligatoryReassign={reassignObligatory}
        helperText={reassignHelperText}
      />
    </Box>
  )
}
